import {
  CANCELABLE_DEVICE_BUYBACK_STATUSES,
  DeviceBuybackStatus,
  textDeviceBuybackStatus,
  textIdentificationKind,
} from '@maru44/huntre-utils/src/models/deviceBuyback'
import { Alert, AlertTitle, Box, Button, Chip, Divider, Paper, Stack, Typography } from '@mui/material'
import { useCallback, useState } from 'react'
import { toast } from 'react-hot-toast'
import { useNavigate, useParams } from 'react-router-dom'
import { buybackShipping } from 'src/config'
import { useDeviceBuyback } from 'src/hooks/deviceBuyback/useDeviceBuyback'
import { formatTimestamp } from 'src/utils/firebase'
import { ResubmitForm } from './ResubmitForm'

export const DeviceBuybackPage = () => {
  const { clientId, id } = useParams()
  const navigate = useNavigate()
  const { getDeviceBuyback, cancelDeviceBuyback, resubmitIdentification } = useDeviceBuyback(clientId, id)
  const [isCanceling, setIsCanceling] = useState(false)

  const buyback = getDeviceBuyback.data

  const handleCancel = useCallback(async () => {
    setIsCanceling(true)
    try {
      await cancelDeviceBuyback()
      toast.success('申請をキャンセルしました')
      await getDeviceBuyback.mutate()
    } catch {
      toast.error('キャンセルに失敗しました')
    } finally {
      setIsCanceling(false)
    }
  }, [cancelDeviceBuyback, getDeviceBuyback])

  if (getDeviceBuyback.isLoading || !buyback) return null

  const canCancel = CANCELABLE_DEVICE_BUYBACK_STATUSES.includes(buyback.status)

  return (
    <Box>
      <Stack spacing={3}>
        <Stack direction="row" spacing={2} sx={{ alignItems: 'center' }}>
          <Typography variant="h5">買取申請</Typography>
          <Chip label={textDeviceBuybackStatus(buyback.status)} />
        </Stack>

        {/* ステータスごとに、次に何をすべきかだけを出す */}
        {buyback.status === DeviceBuybackStatus.Applied && (
          <Alert severity="info">
            <AlertTitle>本人確認中です</AlertTitle>
            確認が取れましたら送付先をお知らせします。
            <strong>それまでデバイスは発送しないでください。</strong>
          </Alert>
        )}

        {buyback.status === DeviceBuybackStatus.NeedsFix && (
          <Alert severity="warning">
            <AlertTitle>書類の再提出をお願いします</AlertTitle>
            {buyback.needsFixReason}
          </Alert>
        )}
        {buyback.status === DeviceBuybackStatus.NeedsFix && (
          <ResubmitForm
            onSubmit={async (kind, file) => {
              await resubmitIdentification(kind, file)
              await getDeviceBuyback.mutate()
            }}
          />
        )}

        {buyback.status === DeviceBuybackStatus.Approved && (
          <Alert severity="success">
            <AlertTitle>本人確認が完了しました。デバイスを発送してください</AlertTitle>
            <Stack spacing={0.5} sx={{ mt: 1 }}>
              <Typography variant="body2">〒{buybackShipping.postalCode}</Typography>
              <Typography variant="body2">{buybackShipping.address}</Typography>
              <Typography variant="body2">{buybackShipping.name} 宛</Typography>
              {buyback.shippingDeadline && (
                <Typography variant="body2" sx={{ mt: 1, fontWeight: 700 }}>
                  {formatTimestamp(buyback.shippingDeadline)} までに発送してください
                </Typography>
              )}
            </Stack>
          </Alert>
        )}

        {buyback.status === DeviceBuybackStatus.Expired && (
          <Alert severity="error">
            <AlertTitle>発送期限が過ぎました</AlertTitle>
            お手数ですが、もう一度申請してください。
            <Box sx={{ mt: 1 }}>
              <Button size="small" variant="outlined" onClick={() => navigate(`/clients/${clientId}/buybacks/new`)}>
                再申請する
              </Button>
            </Box>
          </Alert>
        )}

        {buyback.status === DeviceBuybackStatus.Rejected && (
          <Alert severity="error">
            <AlertTitle>申請は受け付けられませんでした</AlertTitle>
            {buyback.rejectedReason}
          </Alert>
        )}

        <Paper sx={{ p: 3 }}>
          <Stack spacing={2}>
            <Box>
              <Typography variant="body2" sx={{ color: 'text.secondary', mb: 0.5 }}>
                デバイス ({buyback.items?.length ?? 0} 台)
              </Typography>
              <Stack spacing={0.5}>
                {buyback.items?.map((item) => (
                  <Stack key={item.deviceId} direction="row" spacing={2} sx={{ justifyContent: 'space-between' }}>
                    <Typography variant="body2">{item.deviceId}</Typography>
                    <Typography variant="body2">{item.assessedPrice == null ? '査定前' : `${item.assessedPrice.toLocaleString()} 円`}</Typography>
                  </Stack>
                ))}
              </Stack>
            </Box>
            <Divider />
            <Row label="申請日" value={buyback.createdAt ? formatTimestamp(buyback.createdAt) : '-'} />
            <Divider />
            <Row label="本人確認書類" value={textIdentificationKind(buyback.identificationKind)} />
            <Divider />
            <Row
              label="買取金額 (合計)"
              value={
                buyback.totalPrice == null
                  ? `査定前 (1 台あたり 最低 ${buyback.minimumPrice.toLocaleString()} 円 〜 上限 ${buyback.maximumPrice.toLocaleString()} 円)`
                  : `${buyback.totalPrice.toLocaleString()} 円`
              }
            />
            <Divider />
            <Row label="振込日" value={buyback.paidAt ? formatTimestamp(buyback.paidAt) : '-'} />
            {buyback.applicantNote && (
              <>
                <Divider />
                <Row label="備考" value={buyback.applicantNote} />
              </>
            )}
          </Stack>
        </Paper>

        {canCancel && (
          <Box>
            <Button color="error" variant="outlined" disabled={isCanceling} onClick={handleCancel}>
              申請をキャンセルする
            </Button>
          </Box>
        )}
      </Stack>
    </Box>
  )
}

const Row = ({ label, value }: { label: string; value: string }) => (
  <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.5, sm: 2 }}>
    <Typography variant="body2" sx={{ color: 'text.secondary', minWidth: 140 }}>
      {label}
    </Typography>
    <Typography variant="body2">{value}</Typography>
  </Stack>
)
